/**
 * The play log's arithmetic, away from the screen that draws it.
 *
 * ⚠️ THE SAME SPLIT `studentsModel.ts` MAKES, FOR THE SAME REASON. Every number
 * on that table is a claim about a child that a head teacher may repeat to a
 * parent, and a claim computed inside JSX is a claim with no test.
 */

import type { GameLogRow, SchoolGameLog } from '../../../shared/api/school'
import { neutraliseCell } from '../../../shared/lib/exportData'
import { formatIst } from '../../../shared/lib/ist'

/**
 * Answered-correct out of ANSWERED — never out of rounds.
 *
 * 🔴 NULL WHEN NOTHING WAS ANSWERED, RATHER THAN ZERO. Kalqy World is a gesture
 * game: a sitting of it records acts, not answers. Printing "0%" beside one
 * would tell a school that a child got everything wrong when the truth is that
 * this game does not ask questions. The backend's `accuracyOf` makes the same
 * call; this is the browser's copy of one rule, not a second rule.
 */
export function accuracyOf(row: Pick<GameLogRow, 'correct' | 'wrong'>): number | null {
  const answered = row.correct + row.wrong
  return answered === 0 ? null : Math.round((row.correct / answered) * 100)
}

/**
 * How long the sitting ran, in whole minutes.
 *
 * ⚠️ NULL WHILE IT IS STILL OPEN. `Session.endedAt` is nullable because a tablet
 * that goes flat mid-round never closes its sitting — measuring to `now` would
 * grow a row's duration every time the page refreshed, and a sitting from March
 * would read as three weeks long.
 */
export function durationMinutes(row: Pick<GameLogRow, 'playedAt' | 'endedAt'>): number | null {
  if (row.endedAt === null) return null
  const ms = new Date(row.endedAt).getTime() - new Date(row.playedAt).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  return Math.max(1, Math.round(ms / 60_000))
}

/**
 * The pages the server's `total` implies.
 *
 * 🔴 `total` COUNTS SITTINGS AND THE TABLE DRAWS ROWS, SO THIS IS THE ONLY
 * HONEST PAGE COUNT. A sitting that spanned two games is one unit of paging and
 * two lines on screen; dividing `rows.length` would invent pages that do not
 * exist and leave the reader on an empty one.
 */
export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0) return 1
  return Math.max(1, Math.ceil(total / pageSize))
}

/**
 * What this page is showing, as a sentence rather than a ratio.
 *
 * ⚠️ IT COUNTS SITTINGS, MATCHING `total`, AND SAYS THE WORD. "1–25 of 340"
 * beside a table of 31 lines is the sort of disagreement that makes a reader
 * stop trusting both numbers.
 */
export function rangeLabel(log: Pick<SchoolGameLog, 'total' | 'page' | 'pageSize'>): string {
  if (log.total === 0) return 'No sittings'
  const first = (log.page - 1) * log.pageSize + 1
  const last = Math.min(log.page * log.pageSize, log.total)
  if (first > log.total) return `No sittings on this page of ${String(log.total)}`
  const noun = log.total === 1 ? 'sitting' : 'sittings'
  return `${String(first)}\u2013${String(last)} of ${String(log.total)} ${noun}`
}

/**
 * The log as a spreadsheet.
 *
 * 🔴 EVERY OTHER SCHOOL TABLE EXPORTS, AND THIS IS THE ONE A SCHOOL MOST WANTS
 * OUT: it is the evidence behind "your child has played four times this term".
 *
 * ⚠️ IT QUOTES AND DOUBLES QUOTES, because a child's name may contain a comma
 * and a class may be called `Nursery "A"`. `studentsToCsv` escapes the same way.
 *
 * ⚠️ AN ABSENT TEACHER IS SPELLED OUT RATHER THAN LEFT BLANK. An empty cell in a
 * spreadsheet reads as data that went missing; "Outside class" is the fact.
 */
export function gameLogToCsv(rows: readonly GameLogRow[], gameTitle: (id: string) => string): string {
  /*
   * 🔴 NEUTRALISED BEFORE QUOTING (CWE-1236). A child's or class's name is
   * typed by a person, and one starting `=` runs as a formula when the file is
   * opened in Excel — `neutraliseCell` is the rule `exportData.ts` and the
   * server's `csvCell` share. Numbers pass through untouched.
   */
  const cell = (v: string | number | null): string => {
    if (v === null) return ''
    const s = neutraliseCell(v)
    return /[",\n\r]/.test(s) ? `"${s.split('"').join('""')}"` : s
  }
  const head = [
    'Played at',
    'Ended at',
    'Student',
    'Roll',
    'Class',
    'Game',
    'Teacher',
    'Rounds',
    'Correct',
    'Wrong',
    'Accuracy %',
    'Highest level',
    'Minutes',
  ]
  const lines = [head.join(',')]
  for (const row of rows) {
    lines.push(
      [
        cell(row.playedAt),
        cell(row.endedAt),
        cell(row.studentName),
        cell(row.rollNumber),
        cell(row.className),
        cell(gameTitle(row.game)),
        cell(row.teacherName ?? 'Outside class'),
        cell(row.rounds),
        cell(row.correct),
        cell(row.wrong),
        cell(accuracyOf(row)),
        cell(row.highestLevel),
        cell(durationMinutes(row)),
      ].join(','),
    )
  }
  return lines.join('\n')
}

/**
 * "1 Apr, 2:30 pm" — when a sitting began, as India saw it, whatever zone the
 * laptop is set to. "—" for a value that is not a date.
 */
export function sittingStamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : formatIst(d, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
}
